import * as React from "react"
import * as TooltipPrimitive from "@radix-ui/react-tooltip"
import { cx } from "@/lib/utils/cx"

// Tooltip primitive matching the rest of the new design vocabulary.
// Replaces native `title=` attributes on icon-only controls so hover hints
// don't break the visual language with an OS-styled bubble.
//
// Usage:
//
//   <Tooltip label="Schedule">
//     <button>...</button>
//   </Tooltip>
//
// For more control (different sides per child, controlled state, etc.) use
// the lower-level Root/Trigger/Content exports directly.

const Provider = TooltipPrimitive.Provider
const Root = TooltipPrimitive.Root
const Trigger = TooltipPrimitive.Trigger

const Content = React.forwardRef<
  React.ComponentRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 6, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cx(
        "z-50 px-2.5 py-1.5 rounded-lg text-[11.5px] font-semibold tracking-tight",
        "bg-zinc-900 text-white shadow-lg shadow-zinc-900/15",
        "data-[state=delayed-open]:animate-in data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
        "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
        "data-[side=bottom]:slide-in-from-top-1 data-[side=top]:slide-in-from-bottom-1",
        "data-[side=left]:slide-in-from-right-1 data-[side=right]:slide-in-from-left-1",
        className,
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
))
Content.displayName = TooltipPrimitive.Content.displayName

export interface TooltipProps {
  label: React.ReactNode
  children: React.ReactElement
  side?: "top" | "bottom" | "left" | "right"
  delay?: number
  asChild?: boolean
}

export function Tooltip({
  label,
  children,
  side = "top",
  delay = 250,
}: TooltipProps) {
  return (
    <Provider delayDuration={delay}>
      <Root>
        <Trigger asChild>{children}</Trigger>
        <Content side={side}>{label}</Content>
      </Root>
    </Provider>
  )
}

export {
  Provider as TooltipProvider,
  Root as TooltipRoot,
  Trigger as TooltipTrigger,
  Content as TooltipContent,
}
